import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { useState } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { AnimatedNumber } from '@sorare/core/src/atoms/animations/AnimatedNumber';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Box } from '@sorare/core/src/atoms/icons/Box';
import { ScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BrandS, LabelS, LabelXS } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { SplitProgressBar } from '@sorare/core/src/atoms/ui/SplitProgressBar';
import { ThresholdLabel } from '@sorare/core/src/atoms/ui/SplitProgressBar/ThresholdLabel';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { range } from '@sorare/core/src/lib/arrays';
import { formatScarcity, isVaultableRarity } from '@sorare/core/src/lib/cards';
import { getRarityColor } from '@sorare/core/src/lib/colors';
import { fantasy } from '@sorare/core/src/lib/glossary';
import {
  desktopAndAbove,
  laptopAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';
import type { HexColor } from '@sorare/core/src/style/types';

import { RarityVaultHowItWorksDialogWithFetch } from '#src/components/sealing/RarityVaultHowItWorksDialog/index.withFetch.tsx';
import rewardsBannerBg from '#src/components/sealing/assets/sealing-banner-bg.webp';
import { VAULT_PROGRESS_BAR_COLORS } from '#src/components/sealing/constant.ts';

import type {
  RarityVaultBanner_boostLevel,
  RarityVaultBanner_rewardBoost,
  RarityVaultBanner_sealingConfig,
} from './__generated__/index.graphql.ts';

const Wrapper = styled(Horizontal)`
  --banner-gap: var(--intermediate-unit);
  --banner-height: 68px;
  --contents-min-width: calc(48 * var(--unit)); /* 384px */
  --contents-max-width: 100%;

  min-height: var(--banner-height);
  max-width: 100%;
  width: 100%;
  gap: var(--banner-gap);

  align-items: stretch;
  overflow: hidden;

  &.withBackground {
    --banner-height: 100px;
    background-image: url(${rewardsBannerBg});
    background-size: cover;
    background-position: center;
    outline: 1px solid var(--c-nd-150);
    outline-offset: -1px;
    padding: var(--double-unit);
    border-radius: var(--double-unit);
  }

  @media ${tabletAndAbove} {
    --banner-gap: var(--double-unit);
  }

  @media ${laptopAndAbove} {
    --banner-gap: var(--triple-unit);
  }
  @media ${desktopAndAbove} {
    --banner-gap: var(--quadruple-unit);
  }
`;

const InnerContainer = styled(Vertical)`
  gap: var(--unit);
  flex-grow: 1;
  flex-shrink: 0;

  min-width: min(var(--contents-min-width), 100%);
  max-width: max(var(--contents-min-width), var(--contents-max-width));
`;

const Label = styled(BrandS)`
  text-transform: uppercase;
`;

const BarWrapper = styled(Vertical).attrs({ center: true })`
  flex: 1;
  position: relative;
  isolation: isolate;
`;

const PreviewProgress = styled(LabelXS).attrs({
  bold: true,
  uppercase: true,
  brand: true,
})`
  position: absolute;
  line-height: 14px;
  font-size: 10px;
  z-index: 100;
`;

const InlineHorizontal = styled(Horizontal)`
  display: inline-flex;
`;
type VaultColor = {
  [key in Rarity]: HexColor;
};

const VAULT_COLORS: VaultColor = {
  [Rarity.common]: 'var(--c-collection-common)',
  [Rarity.limited]: 'var(--c-vault-limited)',
  [Rarity.rare]: 'var(--c-vault-rare)',
  [Rarity.super_rare]: 'var(--c-vault-superRare)',
  [Rarity.unique]: 'var(--c-vault-unique)',
  [Rarity.custom_series]: 'var(--c-collection-custom_series)',
};

export type Props = {
  rarity: Rarity;
  points?: RarityVaultBanner_rewardBoost['points'];
  fullRarityVaultMatrix?: RarityVaultBanner_sealingConfig['fullRarityVaultMatrix'];
  additionalPoints?: number;
  showHowItWorksCta?: boolean | 'onlyIcon';
  withBackground?: boolean;
  minimumPointsRequired?: number;
  isBoostLevelRequirementBanner?: boolean;
};

export const RarityVaultBanner = ({
  rarity,
  points: initialPoints = 11,
  fullRarityVaultMatrix = [],
  additionalPoints = 0,
  showHowItWorksCta = true,
  withBackground = true,
  minimumPointsRequired = 0,
  isBoostLevelRequirementBanner = false,
}: Props) => {
  const isLaptop = useIsLaptop();
  const [showHowItWorksDialog, setShowHowItWorksDialog] = useState(false);

  const topDownMatrix = [...fullRarityVaultMatrix].reverse();

  // identify current boost level
  const boostLevelIndexInMatrix = topDownMatrix.findIndex(
    level => level.points <= initialPoints
  );

  if (!isVaultableRarity(rarity)) {
    return null;
  }

  if (boostLevelIndexInMatrix < 0) {
    return null;
  }

  const minVaultBoostLevel = topDownMatrix.find(
    level => level.points <= minimumPointsRequired
  );

  // identify previewed boost level
  const previewMinBoostLevelInMatrix = topDownMatrix.findIndex(
    level => level.points <= initialPoints + additionalPoints
  );
  const previewBoostLevelOnLeft = topDownMatrix[previewMinBoostLevelInMatrix];

  // identify previewed boost next level
  let previewBoostLevelOnRight =
    topDownMatrix[previewMinBoostLevelInMatrix - 1];
  if (!previewBoostLevelOnRight) {
    // if no next level, create a fake one with just 1 point (1 segment)
    previewBoostLevelOnRight = {
      ...topDownMatrix[previewMinBoostLevelInMatrix],
      points: topDownMatrix[previewMinBoostLevelInMatrix].points + 1,
    };
  }

  // calculate current progress on the previewed boost level
  const currentProgress = Math.max(
    0,
    Math.min(previewBoostLevelOnRight.points, initialPoints) -
      previewBoostLevelOnLeft.points
  );

  // calculate previewed progress on the previewed boost level
  const previewProgress = Math.max(
    0,
    initialPoints + additionalPoints - previewBoostLevelOnLeft.points
  );

  // check if the current boost level was the max boost level
  const reachedMaxBoostLevel = boostLevelIndexInMatrix === 0;

  // check if the previewed progress is reaching the next boost level
  const reachingNextBoostLevel =
    previewBoostLevelOnRight.points - previewBoostLevelOnLeft.points ===
    previewProgress;

  // check if the previewed progress is reaching the max boost level
  const reachingMaxBoostLevel =
    previewBoostLevelOnRight.milestone === previewBoostLevelOnLeft.milestone;

  const isLevellingUp =
    topDownMatrix[boostLevelIndexInMatrix].milestone <
    topDownMatrix[previewMinBoostLevelInMatrix].milestone;

  const pointsToLevelUp = Math.max(
    0,
    minimumPointsRequired - initialPoints - additionalPoints
  );

  return (
    <>
      <Wrapper
        color={VAULT_COLORS[rarity]}
        className={classNames({
          withBackground,
        })}
      >
        <InnerContainer>
          <SBHorizontal>
            <Horizontal>
              <Label>
                <FormattedMessage
                  defaultMessage="{rarity} vault"
                  values={{ rarity: formatScarcity(rarity) }}
                />
              </Label>
              <BrandS color={getRarityColor(rarity)} uppercase>
                <FormattedMessage
                  {...fantasy.levelNumber}
                  values={{ level: previewBoostLevelOnLeft.milestone }}
                />
              </BrandS>
              {!!isLevellingUp && (
                <Chip color="purple">
                  <FormattedMessage defaultMessage="Lvl Up!" />
                </Chip>
              )}
            </Horizontal>
            {!!showHowItWorksCta && (
              <Button
                onClick={() => setShowHowItWorksDialog(true)}
                size="compact"
                color="quaternary"
              >
                <FontAwesomeIcon icon={faCircleInfo} />
                {!!isLaptop && showHowItWorksCta !== 'onlyIcon' && (
                  <FormattedMessage defaultMessage="How it works" />
                )}
              </Button>
            )}
          </SBHorizontal>
          <BarWrapper>
            {!isBoostLevelRequirementBanner && (
              <PreviewProgress color="var(--c-white)">
                <FormattedNumber
                  value={initialPoints + additionalPoints}
                  maximumFractionDigits={0}
                />
                /
                <FormattedNumber
                  value={previewBoostLevelOnRight.points}
                  maximumFractionDigits={0}
                />
              </PreviewProgress>
            )}
            {isBoostLevelRequirementBanner ? (
              <SplitProgressBar
                thresholds={range(topDownMatrix[0].milestone).map(i => ({
                  score: i + 1,
                }))}
                currentScore={previewBoostLevelOnLeft?.milestone}
                color={VAULT_PROGRESS_BAR_COLORS[rarity]}
                variant="oblique"
                targetScore={minVaultBoostLevel?.milestone}
              />
            ) : (
              <SplitProgressBar
                thresholds={range(
                  previewBoostLevelOnRight.points -
                    previewBoostLevelOnLeft.points
                ).map(i => ({
                  score: i + 1,
                }))}
                currentScore={
                  reachedMaxBoostLevel && reachingMaxBoostLevel
                    ? 1
                    : currentProgress
                }
                previewScore={reachingMaxBoostLevel ? 1 : previewProgress}
                color={VAULT_PROGRESS_BAR_COLORS[rarity]}
                variant="oblique"
                split={false}
              />
            )}
          </BarWrapper>
          <SBHorizontal>
            {isBoostLevelRequirementBanner ? (
              <LabelS
                as={InlineHorizontal}
                gap={0.5}
                bold
                color={
                  pointsToLevelUp > 0
                    ? 'var(--c-nd-600)'
                    : 'var(--c-onBase-success)'
                }
              >
                {pointsToLevelUp > 0 ? (
                  <FormattedMessage
                    defaultMessage="{nbCards} / to level {targetLevel}"
                    values={{
                      nbCards: (
                        <InlineHorizontal gap={0.5}>
                          <ScarcityIcon rarity={rarity} size={1.5} />
                          <LabelS bold as="span" color="var(--c-white)">
                            {pointsToLevelUp}
                          </LabelS>
                        </InlineHorizontal>
                      ),
                      targetLevel: minVaultBoostLevel?.milestone,
                    }}
                  />
                ) : (
                  <div>
                    <FormattedMessage defaultMessage="Unlocked!" />
                  </div>
                )}
              </LabelS>
            ) : (
              <ThresholdLabel
                shiny
                color="green"
                bonus={previewBoostLevelOnLeft.boostBasisPoints / 10000}
                icon={<Box size={1.5} />}
                row
              />
            )}
            {additionalPoints !== 0 && (
              <BrandS
                uppercase
                bold
                color={
                  additionalPoints > 0
                    ? 'var(--c-onBase-success)'
                    : 'var(--c-red-400)'
                }
                aria-label={`+${additionalPoints}`}
              >
                <AnimatedNumber
                  value={additionalPoints}
                  formatNumberProps={{
                    signDisplay: 'always',
                    maximumFractionDigits: 0,
                  }}
                />
              </BrandS>
            )}
            <ThresholdLabel
              shiny={reachingNextBoostLevel || reachingMaxBoostLevel}
              bonus={previewBoostLevelOnRight.boostBasisPoints / 10000}
              icon={<Box size={1.5} />}
              row
            />
          </SBHorizontal>
        </InnerContainer>
      </Wrapper>
      <RarityVaultHowItWorksDialogWithFetch
        open={showHowItWorksDialog}
        rarity={rarity}
        onClose={() => setShowHowItWorksDialog(false)}
      />
    </>
  );
};

const fragment_boostLevel = gql`
  fragment RarityVaultBanner_boostLevel on BoostLevel {
    points
    boostBasisPoints
    milestone
  }
` as TypedDocumentNode<RarityVaultBanner_boostLevel>;

const fragment_sealingConfig = gql`
  fragment RarityVaultBanner_sealingConfig on SealingConfig {
    fullRarityVaultMatrix(rarity: $rarity, sport: $sport) {
      ...RarityVaultBanner_boostLevel
    }
  }
  ${fragment_boostLevel}
` as TypedDocumentNode<RarityVaultBanner_sealingConfig>;

const fragment_rewardBoost = gql`
  fragment RarityVaultBanner_rewardBoost on RewardBoost {
    points
  }
  ${fragment_boostLevel}
` as TypedDocumentNode<RarityVaultBanner_rewardBoost>;

RarityVaultBanner.fragments = {
  sealingConfig: fragment_sealingConfig,
  rewardBoost: fragment_rewardBoost,
};
